import get from 'lodash/get'
import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import {
  NavigateFunction,
  Outlet,
  useNavigate,
  useParams,
} from 'react-router-dom'

import mixpanel from '@disco/api/mixpanel'
import {
  addContactNote,
  cancelContactsEditing,
  changeContactValue,
  deleteContactNote,
  deleteContacts,
  loadContactActivity,
  loadContactNotes,
  loadContactsByIds,
  updateContactNote,
  updateContacts,
} from '@disco/state-management/actions/contact-actions'
import {
  listFormStartManagingLists,
  loadEmailGroups,
} from '@disco/state-management/actions/EmailGroupActions'
import {
  getContactsSavingStatus,
  getPros,
  getPublishers,
  getRightsholderPro,
  getRightsholderProDropdownOptions,
  getRightsholderPublisherDropdownOptions,
  getRightsholderPublishers,
  makeGetContact,
} from '@disco/state-management/selectors/contacts'
import {
  getContactCustomFields,
  getHideContactRemovalSetting,
} from '@disco/state-management/selectors/user'
import { pushUINotification } from '@disco/utils/notifications'

import type { Contact } from 'types/list'

import HelpTooltip from 'components/common/tooltip'
import DocumentHeadTags from 'components/document-head-tags'
import Button from 'components/ui/button'
import EllipsisMenu from 'components/ui/ellipsis-menu'
import PageHeader from 'components/ui/page-header'
import PageSection from 'components/ui/page-section'
import UserAvatar from 'components/ui/user-avatar'

import type { RootState } from 'store/configureStore'

import {
  buildExternalIdentifiers,
  getIdentifierValue,
  padIPIIdentifier,
} from 'features/rights-data/utils/identifier-validation'

import ActivityTab from './activity-tab'
import DetailsTab from './details-tab'
import ListsTab from './lists-tab'
import NotesTab from './notes-tab'
import TagsTab from './tags-tab'

type OwnProps = {
  params: {
    contactId: string
  }
  initialTabIndex: number
  navigate: NavigateFunction
}

const mapStateToProps = (state: RootState, props: OwnProps) => {
  const getContact = makeGetContact()

  return {
    contact: getContact(state, {
      contactId: Number(props.params.contactId),
    }),
    emailGroups: state.emailGroups,
    proDropdownOptions: getRightsholderProDropdownOptions(state),
    publisherDropdownOptions: getRightsholderPublisherDropdownOptions(state),
    pros: getPros(state),
    publishers: getPublishers(state),
    rightsholderPro: getRightsholderPro(state),
    rightsholderPublishers: getRightsholderPublishers(state),
    saving: getContactsSavingStatus(state),
    hideContactRemoval: getHideContactRemovalSetting(state),
    customFields: getContactCustomFields(state),
  }
}

const mapDispatchToProps = {
  loadContactsByIds,
  deleteContacts,
  updateContacts,
  changeContactValue,
  addContactNote,
  deleteContactNote,
  updateContactNote,
  listFormStartManagingLists,
  loadContactNotes,
  loadContactActivity,
  loadEmailGroups,
  cancelContactsEditing,
}

// Define connector
const connector = connect(mapStateToProps, mapDispatchToProps)

// Infer props type
type PropsFromRedux = ConnectedProps<typeof connector>

// Component props = Redux props + own props
type Props = PropsFromRedux & OwnProps

// Higher-order component to provide navigate function and params to class component
const withNavigate = (Component: React.ComponentType<any>) => {
  return (props: any) => {
    const navigate = useNavigate()
    const params = useParams<{ contactId: string }>()
    return <Component {...props} navigate={navigate} params={params} />
  }
}

class ContactDetails extends React.Component<
  Props,
  {
    detailsEditMode: boolean
  }
> {
  state = {
    detailsEditMode: false,
  }
  static defaultProps = {
    initialTabIndex: 0,
  }
  _switchToEditMode = () => {
    this.setState({
      detailsEditMode: true,
    })
  }
  _switchToViewMode = () => {
    this.setState({
      detailsEditMode: false,
    })
    this.props.cancelContactsEditing()
  }
  _onFieldChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    this.props.contact &&
    this.props.changeContactValue(
      this.props.contact.id,
      e.target.name,
      e.target.value,
    )
  _onCustomFieldChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    this.props.contact &&
    this.props.changeContactValue(this.props.contact.id, 'custom_data', {
      ...get(
        this.props.contact,
        'pendingChanges.custom_data',
        this.props.contact.custom_data || {},
      ),
      [e.target.name]: e.target.value,
    })
  _onDefaultEntityDetailChange = (
    field: 'default_pro_id' | 'default_publisher_id',
    value: number | null,
  ) => {
    const contact = this.props.contact
    if (!contact) return

    // entity_details is replaced as a whole by the API. Merge with any
    // existing pending value so changing one default does not clear the other.
    const entityDetails = get(
      contact,
      'pendingChanges.entity_details',
      contact.entity_details || {},
    )
    this.props.changeContactValue(contact.id, 'entity_details', {
      ...entityDetails,
      [field]: value,
    } as any)
  }
  _onDefaultProSelect = (selected: { value: string; label: string }) => {
    if (selected === null) {
      this._onDefaultEntityDetailChange('default_pro_id', null)
      return
    }

    const pro = this.props.rightsholderPro.find(
      p => p.name === selected.label.trim(),
    )

    if (pro) {
      this._onDefaultEntityDetailChange('default_pro_id', pro.id)
    }
  }
  _onDefaultPublisherSelect = (
    selected: { value: string; label: string } | null,
  ) => {
    if (selected === null) {
      // This represents a 'cleared' value.
      this._onDefaultEntityDetailChange('default_publisher_id', null)
      return
    }

    const publisher = this.props.rightsholderPublishers.find(
      p => p.name === selected.label.trim(),
    )

    if (publisher) {
      this._onDefaultEntityDetailChange('default_publisher_id', publisher.id)
    }
  }
  _onExternalIdentifierChange = (type: 'ipi' | 'isni', value: string) => {
    const contact = this.props.contact
    if (!contact) return

    const currentIdentifiers = get(
      contact,
      'pendingChanges.external_identifiers',
      contact.external_identifiers || [],
    )
    const currentIpi = getIdentifierValue(currentIdentifiers, 'ipi') || ''
    const currentIsni = getIdentifierValue(currentIdentifiers, 'isni') || ''

    this.props.changeContactValue(
      contact.id,
      'external_identifiers',
      buildExternalIdentifiers(
        type === 'ipi' ? value : currentIpi,
        type === 'isni' ? value : currentIsni,
      ) ?? [],
    )
  }
  _updateContact = () => {
    const { contact } = this.props
    if (contact) {
      const pendingIdentifiers = contact.pendingChanges?.external_identifiers
      const contactToSave = pendingIdentifiers
        ? {
            ...contact,
            pendingChanges: {
              ...contact.pendingChanges,
              external_identifiers: padIPIIdentifier(pendingIdentifiers),
            },
          }
        : contact
      ;(this.props.updateContacts([contactToSave]) as Promise<any>)
        .then(() => {
          mixpanel.sendPureEvent('Edit contact inside single contact view')

          this._switchToViewMode()
        })
        .catch(() => {
          // Don't do anything and stay in edit mode so the user can fix the error (e.g. a duplicate
          // IPI) without losing their changes; updateContacts shows the toast.
        })
    }
  }
  _onActionClick = (action: 'Delete' | 'Edit' | 'ChangeLists') => {
    const { deleteContacts, contact, hideContactRemoval, navigate } = this.props
    if (!contact) return

    switch (action) {
      case 'Delete':
        if (hideContactRemoval) {
          deleteContacts([contact.id])
          return navigate('/contacts')
        } else {
          return navigate(`/contacts/${this.props.params.contactId}/delete`)
        }

      case 'ChangeLists':
        this.props.listFormStartManagingLists([contact], 'MULTIPLE_LISTS')
        break

      default:
        throw new Error('Not implemented')
    }
  }
  _loadContactDetails = (contact: Contact | null | undefined) => {
    if (contact && !contact.notes) {
      this.props.loadContactNotes(contact.id)
    }

    if (contact) {
      this.props.loadContactActivity(contact.id)
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const currentContactId = get(this.props.contact, 'id', null)
    const nextContactId = get(nextProps.contact, 'id', null)

    if (nextContactId && currentContactId !== nextContactId) {
      this._loadContactDetails(nextProps.contact)
    }

    if (
      nextProps.contact &&
      this._isActivityTabHidden(nextProps.contact) &&
      this.props.initialTabIndex === 1
    ) {
      this.props.navigate(`/contacts/${nextProps.contact.id}`)
    }
  }

  componentDidMount() {
    this.props
      .loadContactsByIds([Number(this.props.params.contactId)])
      .catch(() => {
        pushUINotification({
          message: 'Sorry, we couldn’t load this contact.',
          level: 'error',
        })
      })

    this._loadContactDetails(this.props.contact)

    this.props.loadEmailGroups()
    mixpanel.sendPureEvent('View single contact')
  }

  _menuOptions() {
    if (!this.props.contact) return []
    const nonListContact = !this.props.contact.email
    let changeListItem = {
      value: 'ChangeLists',
      label: <span>Add or remove from lists</span>,
      disabled: false,
    }

    if (nonListContact) {
      changeListItem.label = (
        <span className="ellipsis-menu__button--disabled">
          Add or remove from lists
          <HelpTooltip>
            Contact without email could not be added to list.
          </HelpTooltip>
        </span>
      )
      changeListItem.disabled = true
    }

    return [
      changeListItem,
      {
        value: 'Delete',
        label: 'Delete',
        destructive: true,
      },
    ]
  }

  _isActivityTabHidden(contact: Contact): boolean {
    const { is_team_member, referenced_business } = contact
    if (referenced_business) return true
    if (is_team_member) return true
    return false
  }

  render() {
    const { contact, changeContactValue, customFields } = this.props
    const { detailsEditMode } = this.state

    if (!contact) {
      return null
    }

    return (
      <React.Fragment>
        <DocumentHeadTags title="Contact details | DISCO" />
        <PageHeader
          breadcrumbs={{
            content: 'Back to contacts',
            link: '/contacts/list',
          }}
          title={contact.name || 'Unnamed contact'}
          titleBefore={<UserAvatar name={contact.name} />}
          actions={
            <EllipsisMenu
              compact
              caption="Actions"
              options={this._menuOptions()}
              onOptionClick={this._onActionClick}
            />
          }
        />
        <PageSection
          title="Details" // description="We can use some help text here that can go into two lines"
        >
          <DetailsTab
            editMode={detailsEditMode}
            contact={contact}
            customFields={customFields}
            onUpdate={this._updateContact}
            onCancel={this._switchToViewMode}
            onFieldChange={this._onFieldChange}
            onCustomFieldChange={this._onCustomFieldChange}
            onDefaultProSelect={this._onDefaultProSelect}
            onDefaultPublisherSelect={this._onDefaultPublisherSelect}
            onExternalIdentifierChange={this._onExternalIdentifierChange}
            onSwitchToEditMode={this._switchToEditMode}
            pros={this.props.pros}
            publishers={this.props.publishers}
            rightsholderPublishers={this.props.rightsholderPublishers}
            rightsholderPro={this.props.rightsholderPro}
            proDropdownOptions={this.props.proDropdownOptions}
            publisherDropdownOptions={this.props.publisherDropdownOptions}
            saving={this.props.saving}
          />
        </PageSection>
        <PageSection
          title="Tags" // description="We can use some help text here that can go into two lines"
        >
          <TagsTab
            contact={contact}
            changeContactValue={changeContactValue}
            updateContacts={this.props.updateContacts}
            cancelContactsEditing={this.props.cancelContactsEditing}
          />
        </PageSection>
        <PageSection
          title="Notes" // description="We can use some help text here that can go into two lines"
        >
          <NotesTab
            contact={contact}
            addContactNote={this.props.addContactNote}
            deleteContactNote={this.props.deleteContactNote}
            updateContactNote={this.props.updateContactNote}
          />
        </PageSection>
        {contact.email && (
          <PageSection
            title="Mailing Lists" // description="We can use some help text here that can go into two lines"
          >
            <ListsTab
              contact={contact}
              emailGroups={this.props.emailGroups}
              listFormStartManagingLists={this.props.listFormStartManagingLists}
            />
          </PageSection>
        )}
        {/*
         https://app.asana.com/0/364097350002545/402398786337192/f we haven't
         figured activity for business inbox contacts yet
        */}
        {!this._isActivityTabHidden(contact) && (
          <PageSection
            title="Activity"
            description={
              contact.activity &&
              contact.activity.length > 5 && (
                <Button
                  appearance="inline-link"
                  caption="Click here to view all "
                  link={`/contacts/${contact.id}/activity`}
                />
              )
            }
          >
            <ActivityTab contact={contact} />
          </PageSection>
        )}
        <Outlet />
      </React.Fragment>
    )
  }
}

const ConnectedContactDetails = connector(ContactDetails)
export default withNavigate(ConnectedContactDetails)
